import { useEffect, useState } from "react";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Button } from "@/components/ui/buttons/button";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { useDuel } from "@/providers/duel-provider";
import { useUser } from "@/providers/user-provider";
import { isXsScreen } from "@/utils/screen-size-check";
import { TeamSpritesDisplay } from "@/components/pokemon/team-sprites-display";

export const FindingOpponent = ({
  handleCancel,
  countryCode,
  isCancelling,
  team,
}: {
  handleCancel: () => void;
  countryCode?: string | null;
  isCancelling?: boolean;
  team?: Array<{ id: number; base_pokemon_name: string; [key: string]: any }>;
}) => {
  const [dots, setDots] = useState("");
  const { isReconnecting } = useDuel();
  const { playerElo } = useUser();

  // Animated loading dots and text cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return "";
        return prev + ".";
      });
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="relative z-10 flex flex-col justify-center items-center gap-8 p-4 w-full h-full">
      {/* ELO Badge - top (theme colors) */}
      {playerElo !== null && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20">
          <div className="relative flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-pk-gold-4 bg-gradient-to-br from-pk-gold-1 to-pk-gold-2 shadow-cartoon-sm backdrop-blur-sm">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-pk-night-1/40 to-transparent pointer-events-none" />
            <img
              src="/assets/icons/medal.png"
              alt=""
              className="w-8 h-8 object-contain drop-shadow-md mt-auto"
            />
            <div className="relative flex flex-col items-start">
              <span className="text-[10px] font-bold text-pk-gold-5 uppercase tracking-widest">
                Rating
              </span>
              <span className="text-xl xs:text-2xl font-black text-pk-cream-1 tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                {playerElo}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Status Text */}
      <div className="flex flex-col items-center gap-4 z-20">
        {countryCode && (
          <div className="animate-bounce">
            <CountryFlag countryCode={countryCode} size="lg" />
          </div>
        )}

        <div className="flex flex-col items-center gap-2">
          <StrokedText
            strokeWidth={8}
            fontSize={isXsScreen() ? 16 : 22}
            fillColor="#e0d6cc"
            strokeColor="#2b2018"
            className="animate-pulse font-serif"
          >
            {isReconnecting
              ? "Reconnecting to Duel"
              : "Searching for Challenger"}
          </StrokedText>
          <StrokedText
            strokeWidth={4}
            fontSize={isXsScreen() ? 16 : 24}
            fillColor="#8ba4b8"
            strokeColor="#1d222e"
          >
            {dots || "..."}
          </StrokedText>
        </div>
      </div>

      {/* Team Display */}
      {team && team.length > 0 && (
        <div className="flex ml-20 mt-12 mr-8 flex-col items-center gap-2">
          <TeamSpritesDisplay team={team} />
        </div>
      )}

      {/* Cancel Button - RPG Style */}
      <Button
        variant="red"
        className={`group relative overflow-hidden border-4 border-[#5c2e2e] bg-[#3d1818] hover:bg-[#5c2e2e] transition-all duration-300 shadow-[0_4px_0_#2b1515] active:shadow-none active:translate-y-[4px] rounded-sm ${
          isCancelling ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={handleCancel}
        disabled={isCancelling}
      >
        <div className="relative z-10 flex items-center gap-2 px-8">
          {isCancelling && (
            <div className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
          )}
          <StrokedText
            strokeWidth={6}
            fontSize={isXsScreen() ? 14 : 20}
            fillColor="#e0d6cc"
            fontFamily="vck"
          >
            {isCancelling ? "Cancelling..." : "Cancel"}
          </StrokedText>
        </div>

        {/* Button Shine Effect */}
        {!isCancelling && (
          <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        )}
      </Button>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};
